import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styles from "./CurvedScrollbar.module.scss";

const OFFSET = 10;
const EXTRA_INSET = 1.35;
const MIN_START_RATIO = 0.72;
const MIN_THUMB = 28;
const SEGMENTS = 64;
const FALLBACK_RADIUS = 28;

function readRadius(container) {
  const styles = getComputedStyle(container);
  const fromVar = parseFloat(styles.getPropertyValue("--card-border-radius"));
  if (!Number.isNaN(fromVar) && fromVar > 0) return fromVar;
  const fromBorder = parseFloat(styles.borderRadius);
  if (!Number.isNaN(fromBorder) && fromBorder > 0) return fromBorder;
  return FALLBACK_RADIUS;
}

function initCurvedScrollbar(container) {
  const content = container.querySelector(`.${styles.scrollContent}`);
  if (!content) return () => {};

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.classList.add(styles.scrollbarSvg);
  svg.setAttribute("aria-hidden", "true");

  const trackPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
  trackPath.classList.add(styles.scrollbarTrack);

  const thumbPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
  thumbPath.classList.add(styles.scrollbarThumb);

  svg.appendChild(trackPath);
  svg.appendChild(thumbPath);
  container.appendChild(svg);

  let pathLength = 0;
  let thumbLength = 50;
  let dragging = false;
  let pointerId = null;

  function updatePath() {
    const w = container.clientWidth;
    const h = container.clientHeight;
    const r = readRadius(container);

    const effectiveRadius = Math.max(Math.min(r - OFFSET * 0.35, h * 0.18, w * 0.12), 14);
    const trackX = w - OFFSET;
    const topY = OFFSET;
    const bottomY = h - OFFSET;
    const cornerX = trackX - effectiveRadius;

    const minStartX = w * MIN_START_RATIO;
    let startX = trackX - effectiveRadius * EXTRA_INSET;
    if (startX < minStartX) startX = minStartX;
    if (startX > cornerX) startX = cornerX;

    const d = `
      M ${startX} ${topY}
      L ${cornerX} ${topY}
      A ${effectiveRadius} ${effectiveRadius} 0 0 1 ${trackX} ${topY + effectiveRadius}
      L ${trackX} ${bottomY - effectiveRadius}
      A ${effectiveRadius} ${effectiveRadius} 0 0 1 ${cornerX} ${bottomY}
      L ${startX} ${bottomY}
    `;
    trackPath.setAttribute("d", d);

    pathLength = trackPath.getTotalLength();
    const ratio = content.clientHeight / Math.max(content.scrollHeight, 1);
    thumbLength = Math.max(MIN_THUMB, pathLength * Math.min(ratio, 0.92));

    updateThumb();
  }

  function updateThumb() {
    const scrollableHeight = content.scrollHeight - content.clientHeight || 1;
    const scrollRatio = content.scrollTop / scrollableHeight;
    const startOffset = (pathLength - thumbLength) * scrollRatio;
    const endOffset = startOffset + thumbLength;

    const points = [];
    for (let i = 0; i <= SEGMENTS; i++) {
      const t = startOffset + ((endOffset - startOffset) / SEGMENTS) * i;
      const p = trackPath.getPointAtLength(t);
      points.push(`${p.x} ${p.y}`);
    }

    const segmentD = `M ${points[0]} ${points.slice(1).map((pt) => `L ${pt}`).join(" ")}`;
    thumbPath.setAttribute("d", segmentD);
  }

  function onPointerDown(e) {
    e.preventDefault();
    dragging = true;
    pointerId = e.pointerId;
    thumbPath.setPointerCapture(pointerId);
  }

  function onPointerMove(e) {
    if (!dragging || e.pointerId !== pointerId) return;
    const rect = container.getBoundingClientRect();
    let ratio = (e.clientY - rect.top) / rect.height;
    ratio = Math.max(0, Math.min(1, ratio));
    content.scrollTop = ratio * (content.scrollHeight - content.clientHeight);
    updateThumb();
  }

  function onPointerUp(e) {
    if (!dragging || e.pointerId !== pointerId) return;
    dragging = false;
    try {
      thumbPath.releasePointerCapture(pointerId);
    } catch {
      /* ignore */
    }
    pointerId = null;
  }

  thumbPath.addEventListener("pointerdown", onPointerDown);
  window.addEventListener("pointermove", onPointerMove);
  window.addEventListener("pointerup", onPointerUp);
  content.addEventListener("scroll", updateThumb, { passive: true });
  window.addEventListener("resize", updatePath);

  const resizeObserver =
    typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(updatePath)
      : null;
  if (resizeObserver) {
    resizeObserver.observe(container);
    resizeObserver.observe(content);
  }

  updatePath();

  return () => {
    thumbPath.removeEventListener("pointerdown", onPointerDown);
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    content.removeEventListener("scroll", updateThumb);
    window.removeEventListener("resize", updatePath);
    if (resizeObserver) resizeObserver.disconnect();
    if (svg.parentNode) svg.parentNode.removeChild(svg);
  };
}

export default function CurvedScrollbar({ children, className, darkMode }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    return initCurvedScrollbar(container);
  }, [darkMode]);

  return (
    <div
      ref={containerRef}
      className={[
        styles.scrollContainer,
        darkMode ? styles.dark : styles.light,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      data-scrollbar
    >
      <div className={styles.scrollContent}>{children}</div>
    </div>
  );
}

CurvedScrollbar.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  darkMode: PropTypes.bool,
};

CurvedScrollbar.defaultProps = {
  className: "",
  darkMode: false,
};
